import React from "react";

import { getResourceProfileLabel } from "Constants/constants";
import * as C from "src/organization/pages/project/environment/ConfigureResourcesPage/ConfigureResourcesPage.style";
import {
  getCpuRamOptions,
  getInstancesOptions,
  defaultSelection
} from "src/organization/pages/project/environment/ConfigureResourcesPage/util";

import ResourceRow from "../ResourceRow";
import {
  InactiveEnvironmentWarning,
  AdminUpdateResourcesWarning
} from "../ResourceStickers";
import Total from "../Total";

import type { TotalResources } from "Reducers/environment/deployment";
import type {
  ResourceTypeOptions,
  ResourceData,
  UpdateTypeHandlerFn,
  UpdateCpuRamHandlerFn,
  UpdateInstancesRamHandlerFn,
  UpdateDiskHandlerFn,
  HandleDiskBlurFn
} from "src/organization/pages/project/environment/ConfigureResourcesPage/types";

type FormProps = {
  formState: ResourceData[];
  isTrial: boolean;
  canUpdateResources?: boolean;
  isUpdateActivityInProgress: boolean;
  updateTypeHandler: UpdateTypeHandlerFn;
  updateCpuRamHandler: UpdateCpuRamHandlerFn;
  showErrors: boolean;
  updateInstancesRamHandler: UpdateInstancesRamHandlerFn;
  handleDiskBlur: HandleDiskBlurFn;
  updateDiskHandler: UpdateDiskHandlerFn;
  totalValues: Omit<TotalResources, "status">;
  isEnvironmentInactive: boolean;
};
const Form = ({
  formState,
  isTrial,
  canUpdateResources,
  isUpdateActivityInProgress,
  updateTypeHandler,
  updateCpuRamHandler,
  showErrors,
  updateInstancesRamHandler,
  handleDiskBlur,
  updateDiskHandler,
  totalValues,
  isEnvironmentInactive
}: FormProps) => {
  return (
    <div>
      {formState?.map((resourceRow, index) => {
        const {
          label,
          resourceType,
          typeValue,
          typeOptions,
          profileSize,
          instanceCount,
          disk,
          diskTooltip,
          diskOnlyText,
          containerProfile,
          minimumCpu,
          minimumRam,
          minimumDisk,
          type,
          profiles,
          instancesRange,
          errors
        } = resourceRow;

        const profile = profiles?.[typeValue];

        const currentCpu = profile?.[profileSize!]?.cpu ?? 0;
        const currentRam = profile?.[profileSize!]?.memory ?? 0;

        const typeSelection = typeOptions.find(
          option => option.value === typeValue
        );

        const cpuRamOptions = getCpuRamOptions({
          profile,
          minimumCpu,
          minimumRam,
          isTrial: !!isTrial,
          instanceCount: instanceCount ?? 1,
          currentCpu,
          currentRam,
          totalValues
        });

        const cpuRamSelection =
          cpuRamOptions.find(option => option.value === profileSize) ??
          defaultSelection;

        const instancesOptions = getInstancesOptions({
          instancesRange,
          isTrial: !!isTrial,
          instanceCount: instanceCount ?? 1,
          currentCpu,
          currentRam,
          totalValues
        });

        const instancesSelection =
          instancesOptions.find(
            option => +option.value === (instanceCount ?? 1)
          ) ?? defaultSelection;

        return (
          <React.Fragment key={`${label}:${type}`}>
            <ResourceRow
              formStateRow={resourceRow}
              isWorker={resourceType === "workers"}
              formIsDisabled={!canUpdateResources || isUpdateActivityInProgress}
              isHigherMemoryProfile={containerProfile === "HIGHER_MEMORY"}
              // TYPE
              typeSelection={typeSelection!}
              typeOnChange={option => {
                // Only update Type when selecting a new dropdown selection
                if (option.value !== resourceType) {
                  updateTypeHandler(option.value as ResourceTypeOptions, index);
                }
              }}
              // CPU & RAM
              cpuRamOptions={[
                {
                  label: getResourceProfileLabel(containerProfile),
                  options: cpuRamOptions
                }
              ]}
              cpuRamValue={cpuRamSelection}
              cpuRamOnChange={option => {
                updateCpuRamHandler(option.value, index);
              }}
              cpuRamError={showErrors ? errors.cpuRam : false}
              // Instance
              instancesOptions={instancesOptions}
              instancesValue={instancesSelection}
              instancesOnChange={option => {
                updateInstancesRamHandler(+option.value, index);
              }}
              instancesOnlyText={resourceType === "services" ? "1" : ""}
              // Disk
              diskValue={(disk as string | undefined) ?? null}
              onDiskBlur={(value, diskValue) => {
                handleDiskBlur(value, diskValue, minimumDisk, index);
              }}
              diskOnChange={(value, diskValue) => {
                updateDiskHandler(value, diskValue, index);
              }}
              diskOnlyText={diskOnlyText}
              diskError={showErrors ? errors.disk : false}
              diskTooltip={diskTooltip}
              showTooltip={typeof disk !== "number"}
            />
            <C.Line hideLast={true} />
          </React.Fragment>
        );
      })}

      <Total
        cpu={totalValues.cpu}
        ram={totalValues.ram}
        instances={totalValues.instances}
        disk={+totalValues.disk.toFixed(2)}
      />
      {!canUpdateResources && <AdminUpdateResourcesWarning />}
      {isEnvironmentInactive && <InactiveEnvironmentWarning />}
    </div>
  );
};

export default Form;
